// Main-thread side of the 2x painter. Pieces are painted in a Web Worker (hd-worker.ts) on OffscreenCanvas and come back
// as ImageBitmaps; until one arrives the 1x city texture under the scene is what the player sees. If Worker/OffscreenCanvas
// is missing, the worker fails to load, or it throws, painting falls back to the old time-sliced main-thread painter.
// `?hdworker=0` forces that fallback. Same interface as startHdPiece, so render.ts treats both alike.
import { buildMap, type GameMap } from '../../common/src/map';
import { HD_SCENES } from '../../common/src/data';
import { hdPieces, startHdPiece, type HdPiece, type HdSceneArt } from './hd-scene';
import type { HdReply, HdRequest } from './hd-worker';

export interface HdPaintJob {
  /** paint for about `ms` (main thread) or just check for the worker's reply; true when the piece is done */
  run(ms: number): boolean;
  /** valid when run() returned true: an HTMLCanvasElement (main thread) or ImageBitmap (worker) */
  canvas: HTMLCanvasElement | ImageBitmap;
  /** the scene's lights and props; the worker only sends them when asked (`withArt`) */
  /** worker-side timings (dev) */
  workerMs?: { paint: number; art: number };
  art?: Omit<HdSceneArt, 'props'> & { props: { canvas: HTMLCanvasElement | ImageBitmap; x: number; y: number; ax: number; ay: number }[] };
}

// read at load: the game rewrites the url (?room=) later
const FORCE_MAIN = typeof location !== 'undefined' && new URLSearchParams(location.search).get('hdworker') === '0';
export const hdWorkerSupported = () => !FORCE_MAIN && typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap !== 'undefined';

export class HdPainter {
  private worker?: Worker;
  private pending = new Map<number, (r: HdReply | null) => void>();
  private nextId = 1;
  /** 'worker' until something goes wrong, then 'main' for good */
  mode: 'worker' | 'main' = 'main';
  constructor(private map: GameMap, workerUrl = new URL('./hd-worker.js' + new URL(import.meta.url).search, import.meta.url)) {
    if (!hdWorkerSupported()) return;
    try {
      const w = new Worker(workerUrl);
      w.onmessage = e => { const r = e.data as HdReply, f = this.pending.get(r.id); this.pending.delete(r.id); f?.(r); };
      w.onerror = e => { e.preventDefault(); this.fail(); };
      w.onmessageerror = () => this.fail();
      this.worker = w; this.mode = 'worker';
    } catch { this.mode = 'main'; }
  }
  /** stop the worker for good (the start page's backdrop does this when PLAY is pressed) */
  dispose() { this.worker?.terminate(); this.worker = undefined; this.pending.clear(); this.mode = 'main'; }
  /** the worker is unusable: answer everything in flight with null (jobs repaint on the main thread) and stay on main */
  private fail() {
    this.mode = 'main'; this.worker?.terminate(); this.worker = undefined;
    const p = [...this.pending.values()]; this.pending.clear(); p.forEach(f => f(null));
  }
  private request(piece: HdPiece, withArt: boolean): Promise<HdReply | null> {
    const index = hdPieces(piece.scene).findIndex(q => q.ox === piece.ox && q.oy === piece.oy);
    const id = this.nextId++, msg: HdRequest = { id, scene: piece.scene.id, index, art: withArt };
    return new Promise(res => {
      this.pending.set(id, res); this.worker!.postMessage(msg);
      setTimeout(() => { if (this.pending.has(id)) { console.warn('hd worker timed out, painting on the main thread'); this.fail(); } }, 60_000);
    });
  }

  start(piece: HdPiece, withArt = true): HdPaintJob {
    const main = () => startHdPiece(this.map, piece);
    if (this.mode === 'main') return main();
    let fallback: ReturnType<typeof startHdPiece> | undefined;
    const job: HdPaintJob = {
      canvas: undefined as unknown as ImageBitmap,
      run: (ms) => {
        if (fallback) { const d = fallback.run(ms); if (d) { job.canvas = fallback.canvas; job.art = fallback.art; } return d; }
        return got;
      },
    };
    let got = false;
    this.request(piece, withArt).then(r => {
      if (!r || 'error' in r) { if (r) { console.warn('hd worker failed, painting on the main thread:', r.error); this.fail(); } fallback = main(); return; }
      job.canvas = r.bitmap; job.workerMs = r.ms;
      if (r.art) { const { bitmaps, props, ...rest } = r.art; job.art = { ...rest, props: props.map(q => ({ canvas: bitmaps[q.b], x: q.x, y: q.y, ax: q.ax, ay: q.ay })) }; }
      got = true;
    });
    return job;
  }
}

/** Test hook (scripts/hd-worker-check.mjs): paint a piece on the main thread and in the worker and return pixel hashes of
 *  both (piece image, props, lights), so the two paths can be compared. Run only while nothing else is painting. */
const fnv = (a: ArrayLike<number>, h = 0x811c9dc5) => { for (let i = 0; i < a.length; i++) h = Math.imul(h ^ a[i], 0x01000193) >>> 0; return h; };
function pixelHash(src: HTMLCanvasElement | ImageBitmap, h?: number) {
  const c = document.createElement('canvas'); c.width = src.width; c.height = src.height;
  const g = c.getContext('2d')!; g.drawImage(src, 0, 0); return fnv(g.getImageData(0, 0, c.width, c.height).data, h);
}
async function compareHdPaint(scene: string, index: number) {
  const map = buildMap(), piece = hdPieces(HD_SCENES.find(s => s.id === scene)!)[index];
  const run = async (painter: HdPainter) => {
    const t0 = performance.now(), job = painter.start(piece, true);
    while (!job.run(1e9)) await new Promise(r => setTimeout(r, 5));
    const img = pixelHash(job.canvas); let props = 0x811c9dc5;
    for (const q of job.art!.props) props = pixelHash(q.canvas, fnv([q.x, q.y, q.ax, q.ay], props));
    const lights = fnv(new TextEncoder().encode(JSON.stringify([job.art!.lights, job.art!.all])));
    return { img, props, lights, nProps: job.art!.props.length, size: [job.canvas.width, job.canvas.height], ms: Math.round(performance.now() - t0), workerMs: job.workerMs, mode: painter.mode };
  };
  const worker = await run(new HdPainter(map));
  const mainP = new HdPainter(map); mainP.mode = 'main';
  return { scene, index, worker, main: await run(mainP) };
}
(globalThis as Record<string, unknown>).__hdCompare = compareHdPaint;
